import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { Carousel, CarouselModule } from 'primeng/carousel';
import { BehaviorSubject } from 'rxjs';

import { IAppointmentSlot } from '@sondermind/data-access/public-facing/provider-calendar';
import { NavigationKeys } from '@sondermind/iris/dropdown';
import { IrisIconComponent } from '@sondermind/iris/icon';
import { IrisIconButtonModule } from '@sondermind/iris/icon-button';

import { IProviderCalendarInitSegmentData } from '../../models/segment/provider-calendar-segment-data.interface';
import { IAppointmentCarouselDate, IAppointmentForCarousel } from './appointment-carousel.interface';

const MAX_NUM_DAYS_VISIBLE: number = 3;
const DEFAULT_TIMES_PER_DATE_INITIAL_BOUNDARY: number = 3;

export interface IAppointmentSlotSelection {
  startTime: Date;
  endTime: Date;
}

@Component({
  selector: 'appointment-carousel',
  imports: [
    CommonModule,
    IrisIconComponent,
    IrisIconButtonModule,
    CarouselModule,
    DatePipe,
  ],
  templateUrl: './appointment-carousel.component.html',
  styleUrls: ['./appointment-carousel.component.scss']
})
export class AppointmentCarouselComponent {
  // Fixes vertical scrolling on mobile and disables horizontal swipe gestures
  // Reference: https://stackoverflow.com/questions/62302234/
  constructor() {
    // Disable touch move to allow vertical scrolling
    Carousel.prototype.onTouchMove = (): void => {
    };
    // Disable touch start to prevent horizontal swipe detection
    Carousel.prototype.onTouchStart = (): void => {
    };
    // Disable touch end to prevent swipe navigation
    Carousel.prototype.onTouchEnd = (): void => {
    };
  }
  @Input() useBrowserTimezone: boolean = false;
  // the number of times you show on a given date before the 'more' link
  @Input() initialNumTimesPerDate: number = DEFAULT_TIMES_PER_DATE_INITIAL_BOUNDARY;
  // when the input of the appointment slots is set, convert the incoming slots
  // to ui elements (carousel dates) and initialize the carousel
  @Input() set appointmentSlots(value: IAppointmentSlot[]) {
    if (value && value.length > 0) {
      this.appointmentCarouselDates = this.processAppointmentSlots(value);
      this.emitSegmentCalendarInitialization(value.length);
      this.initializeCarousel();
    }
  }

  @Output() appointmentSlotSelectionChanged = new EventEmitter<IAppointmentSlotSelection | null>();
  @Output() exitFocusFromCalendarEvent = new EventEmitter<boolean>();
  @Output() appointmentSlotsProcessed = new EventEmitter<IProviderCalendarInitSegmentData>();

  @ViewChild('carousel') carousel!: Carousel;

  appointmentCarouselDates: IAppointmentCarouselDate[] = [];
  finalCarouselPageFirstSlot: IAppointmentForCarousel | undefined;
  numVisible: number = MAX_NUM_DAYS_VISIBLE;
  selectedTime: Date | null = null;
  selectedDate: IAppointmentCarouselDate | null = null;
  selectedDateBelowFold: boolean = false;
  showArrows: boolean = false;
  currentCarouselCol: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  landingPageHeaderSpecialtiesId: string = 'show-more-specialties';

  processAppointmentSlots(slots: IAppointmentSlot[]): IAppointmentCarouselDate[] {
    // create an array of appt carousel dates to publish to the appt carousel
    // dates hold arrays of appt times
    const apptCarouselDates: IAppointmentCarouselDate[] = [];

    // sort the time slots earliest first
    slots.sort((slotA, slotB) => slotA.utcStartTime.getTime() - slotB.utcStartTime.getTime())

      // for each appointment slot
      .forEach((slot: IAppointmentSlot) => {
        // create a ui item for the appointment time
        const apptForCarousel: IAppointmentForCarousel = {
          displayStartTime: this.useBrowserTimezone ? new Date(slot.utcStartTime) : slot.displayStartTime,
          utcStartTime: slot.utcStartTime,
          utcEndTime: slot.utcEndTime
        };

        // look for an existing ui item for the appointment date
        const foundDate: IAppointmentCarouselDate | undefined = apptCarouselDates.find(
          (apptCarouselDate: IAppointmentCarouselDate) => {
            const carouselDate = apptCarouselDate.date;
            const slotDate = slot.utcStartTime;

            return carouselDate.getDate() === slotDate.getDate() &&
              carouselDate.getMonth() === slotDate.getMonth() &&
              carouselDate.getFullYear() === slotDate.getFullYear();
          }
        );

        // if the appointment date ui item exists, add the ui element for the time to it
        if (foundDate) {
          foundDate.appointments.push(apptForCarousel);
        } else {
          // if one does not exist, create a ui element for the date
          // and add the time just the same
          const appointmentCarouselDate: IAppointmentCarouselDate = {
            date: slot.utcStartTime,
            appointments: [apptForCarousel],
            showAllAppointments: false
          };

          apptCarouselDates.push(appointmentCarouselDate);
        }
      });

    // The index of the first date of the final page is equal to
    // the last date index minus the number of days on the last page
    const lastDateIndex = apptCarouselDates.length - 1;
    const finalPageFirstDayAppts = apptCarouselDates[lastDateIndex - (lastDateIndex % this.numVisible)].appointments;

    // eslint-disable-next-line prefer-destructuring
    this.finalCarouselPageFirstSlot = finalPageFirstDayAppts[0];

    return apptCarouselDates;
  }

  private emitSegmentCalendarInitialization(timeSlotsCount: number): void {
    this.appointmentSlotsProcessed.emit({
      calendarDaysCount: this.appointmentCarouselDates.length,
      calendarTimeSlotsCount: timeSlotsCount
    });
  }

  private initializeCarousel(): void {
    if (this.appointmentCarouselDates?.length < 1) { return }

    this.numVisible = this.appointmentCarouselDates.length > MAX_NUM_DAYS_VISIBLE
      ? MAX_NUM_DAYS_VISIBLE
      : this.appointmentCarouselDates.length;

    this.setUpCurrentCalendarColumnSubscription();
  }

  clickAppointmentTime(event: MouseEvent, startTime: Date, endTime: Date, apptDate: IAppointmentCarouselDate): void {
    // if the start time is the selected time, unselect it
    const isDeselecting = this.selectedTime === startTime;
    this.selectedTime = isDeselecting ? null : startTime;
    this.selectedDate = apptDate;

    this.selectedDateBelowFold = apptDate.appointments.findIndex(
      (appt) => appt.utcStartTime.toISOString() === this.selectedTime?.toISOString()) >= MAX_NUM_DAYS_VISIBLE;

    this.appointmentSlotSelectionChanged.emit(isDeselecting ? null : { startTime, endTime });
  }

  seeMoreTimes(apptCarouselDate: IAppointmentCarouselDate): void {
    // eslint-disable-next-line no-param-reassign
    apptCarouselDate.showAllAppointments = true;

    // Wait for DOM to update
    setTimeout(() => {
      // Ensure we do not lose focus on the newly displayed slots
      this.focusSlotByDateAndTime(
        apptCarouselDate.appointments[this.initialNumTimesPerDate].utcStartTime, // next appt on this day
        apptCarouselDate.date
      );
    }, 1);
  }

  /** *********** Below are functions to handle keyboard navigation of the calendar *********** */
  // This creates a unique id for each appointment date+time combo
  createSlotId(timeSlot: Date, date: Date): string {
    return `slot${timeSlot.getTime()}${date.getTime()}`;
  }

  createMoreButtonId(apptDate: Date): string {
    return `more${apptDate.getTime()}`;
  }

  focusSlotByDateAndTime(timeSlot: Date, date: Date): void {
    const idToFocus = this.createSlotId(timeSlot, date);
    const elemToFocus = document.getElementById(idToFocus);
    if (elemToFocus) {
      elemToFocus.focus();
    }
  }

  focusCalendarDateMoreButton(date: Date): void {
    const idToFocus = this.createMoreButtonId(date);
    const elemToFocus = document.getElementById(idToFocus);
    if (elemToFocus) {
      elemToFocus.focus();
    }
  }

  setUpCurrentCalendarColumnSubscription(): void {
    this.currentCarouselCol.subscribe((newCol) => {
      // This triggers onCarouselPage for every other column of dates,
      // starting on the 2nd column (the "middle" column upon initial load)
      // Adding 1 also prevents issues when naving onto the first column
      if ((newCol + 1) % 2 === 0) {
        const nextDatePage = Math.floor(newCol / MAX_NUM_DAYS_VISIBLE);
        this.onCarouselPage({ page: nextDatePage });
      }
    });
  }

  onCarouselPage(event: { page: number; }): void {
    const selectedDatePage = Math.floor(
      this.appointmentCarouselDates.findIndex((date) => date.date === this.selectedDate?.date) / MAX_NUM_DAYS_VISIBLE
    );
    const shouldExpandPageToShowSelectedDate = event.page === selectedDatePage && this.selectedDateBelowFold;

    // timeout to match the animation of the carousel
    setTimeout(() => {
      this.appointmentCarouselDates.forEach((date) => {
        // eslint-disable-next-line no-param-reassign
        date.showAllAppointments = shouldExpandPageToShowSelectedDate
          && date.date.toISOString() === this.selectedDate?.date.toISOString();
      });
    }, 100);
  }

  updateCarouselPage(apptDate: IAppointmentCarouselDate, timeSlot: Date): void {
    const dateIndex = this.appointmentCarouselDates.findIndex((date) => date.date === apptDate.date);
    const newPageNum = Math.floor(dateIndex / MAX_NUM_DAYS_VISIBLE);

    // Reduce the page count when tabbing backwards
    // Avoids tab focus from being lost if the previous page does not show
    if (newPageNum < this.carousel.page) {
      this.carousel.page = newPageNum;
    }

    // Move to the final page number, so that the navigation buttons are enabled/disabled correctly
    // Don't increase the page # moving forward because there are animation issues + tab focus is maintained already
    if (timeSlot === this.finalCarouselPageFirstSlot?.displayStartTime) {
      this.carousel.page = Math.floor(this.appointmentCarouselDates.length / MAX_NUM_DAYS_VISIBLE);
    }
  }

  handleSlotKeydown(event: KeyboardEvent, timeSlot: IAppointmentForCarousel, apptDate: IAppointmentCarouselDate): void {
    // Handle additional logic to track collapse/show while tabbing
    if (event.key === 'Tab') {
      const { slotIndex, dateIndex } = this.getIndexOfCurrentSlot(timeSlot, apptDate);
      if (dateIndex !== this.currentCarouselCol.value) {
        this.currentCarouselCol.next(dateIndex);
      }
    }

    // Nav within a column
    if (event.key === NavigationKeys.ArrowDown) {
      event.preventDefault();
      this.handleVerticalCalendarNav(1, timeSlot, apptDate);
    }
    if (event.key === NavigationKeys.ArrowUp) {
      event.preventDefault();
      this.handleVerticalCalendarNav(-1, timeSlot, apptDate);
    }

    // Nav to a slot in the prev/next column over
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.handleHorizontalCalendarNav(1, timeSlot, apptDate);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.handleHorizontalCalendarNav(-1, timeSlot, apptDate);
    }
  }

  private handleVerticalCalendarNav(
    direction: number, timeSlot: IAppointmentForCarousel, apptDate: IAppointmentCarouselDate
  ): void {
    const { slotIndex, dateIndex } = this.getIndexOfCurrentSlot(timeSlot, apptDate);
    const currentDate = this.appointmentCarouselDates[dateIndex];

    const isMovingDown = direction > 0;
    const isMovingUp = direction < 0;
    const hasReachedLastVisibleSlot = slotIndex === this.initialNumTimesPerDate - 1 && !currentDate.showAllAppointments;
    const hasReachedLastSlot = slotIndex === currentDate.appointments.length - 1;
    const hasReachedFirstSlot = slotIndex === 0;

    if (isMovingDown && hasReachedLastSlot) {
      // Move focus to outside the calendar
      // Let the parent handle it since we don't know whether the next element is session preference or submit button
      this.exitFocusFromCalendarEvent.emit();
    } else if (isMovingDown && hasReachedLastVisibleSlot) {
      // Move focus to the more button of the column
      this.focusCalendarDateMoreButton(currentDate.date);
    } else if (isMovingUp && hasReachedFirstSlot) {
      // Move focus to the header
      document.getElementById(this.landingPageHeaderSpecialtiesId)?.focus();
    } else {
      const nextSlot = currentDate.appointments[slotIndex + direction];
      this.focusSlotByDateAndTime(
        nextSlot.utcStartTime, currentDate.date
      );
    }
  }

  private handleHorizontalCalendarNav(
    direction: number, timeSlot: IAppointmentForCarousel, apptDate: IAppointmentCarouselDate
  ): void {
    const { slotIndex, dateIndex } = this.getIndexOfCurrentSlot(timeSlot, apptDate);

    const isMovingRight = direction > 0;
    const isMovingLeft = direction < 0;
    const isWithinRightBoundary = dateIndex + 1 < this.appointmentCarouselDates.length;
    const isWithinLeftBoundary = dateIndex - 1 >= 0;

    let nextDate = null;
    if (isMovingRight && isWithinRightBoundary) {
      nextDate = this.appointmentCarouselDates[dateIndex + direction];
    } else if (isMovingLeft && isWithinLeftBoundary) {
      nextDate = this.appointmentCarouselDates[dateIndex + direction];
    }

    if (nextDate) {
      const totalNumSlotsNextDate = nextDate.appointments.length - 1;
      const isNextDateExpanded = nextDate.showAllAppointments;
      let nextSlotIndex = slotIndex > totalNumSlotsNextDate ? totalNumSlotsNextDate : slotIndex;
      if (!isNextDateExpanded && slotIndex >= MAX_NUM_DAYS_VISIBLE) {
        nextSlotIndex = MAX_NUM_DAYS_VISIBLE - 1;
      }

      this.currentCarouselCol.next(this.currentCarouselCol.value + direction);

      this.focusSlotByDateAndTime(nextDate.appointments[nextSlotIndex].utcStartTime, nextDate.date);
    }
  }

  handleMoreButtonKeydown(event: KeyboardEvent, apptDate: IAppointmentCarouselDate): void {
    // Handle additional logic to track collapse/show while tabbing
    if (event.key === 'Tab') {
      const dateIndex = this.appointmentCarouselDates.findIndex((date) => date.date === apptDate.date);
      if (dateIndex !== this.currentCarouselCol.value) {
        this.currentCarouselCol.next(dateIndex);
      }
    }

    // Nav within a column
    if (event.key === NavigationKeys.ArrowDown) {
      event.preventDefault();
      this.exitFocusFromCalendarEvent.emit();
    }
    if (event.key === NavigationKeys.ArrowUp) {
      event.preventDefault();
      this.focusSlotByDateAndTime(apptDate.appointments[MAX_NUM_DAYS_VISIBLE - 1].utcStartTime, apptDate.date);
    }

    // Nav to a slot in the prev/next column over
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.handleMoreButtonHorizontalNav(apptDate, 1);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.handleMoreButtonHorizontalNav(apptDate, -1);
    }
  }

  private handleMoreButtonHorizontalNav(apptDate: IAppointmentCarouselDate, direction: number): void {
    const dateIndex = this.appointmentCarouselDates.findIndex((date) => date.date === apptDate.date);
    const isMovingRight = direction > 0;
    const isMovingLeft = direction < 0;
    const isWithinRightBoundary = dateIndex + 1 < this.appointmentCarouselDates.length;
    const isWithinLeftBoundary = dateIndex - 1 >= 0;

    let nextDate = null;
    if (isMovingRight && isWithinRightBoundary) {
      nextDate = this.appointmentCarouselDates[dateIndex + direction];
    } else if (isMovingLeft && isWithinLeftBoundary) {
      nextDate = this.appointmentCarouselDates[dateIndex + direction];
    }

    if (nextDate) {
      if (!nextDate.showAllAppointments) {
        this.focusCalendarDateMoreButton(nextDate.date);
      } else {
        this.focusSlotByDateAndTime(
          nextDate.appointments[MAX_NUM_DAYS_VISIBLE].utcStartTime,
          nextDate.date
        );
      }
    }
  }

  private getIndexOfCurrentSlot(
    timeSlot: IAppointmentForCarousel, apptDate: IAppointmentCarouselDate
  ): { slotIndex: number; dateIndex: number; } {
    const dateIndex = this.appointmentCarouselDates.findIndex((date) => date.date === apptDate.date);
    const slotIndex = apptDate.appointments.findIndex(
      (slot) => slot.utcStartTime === timeSlot.utcStartTime
    );
    return { slotIndex, dateIndex };
  }
}
